import { Fragment, FunctionComponent, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { Message } from "primereact/message";
import { Slider } from "primereact/slider";

import { faFileArrowDown } from "@fortawesome/pro-regular-svg-icons";
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { ApiResponse, InitOverrideFunction, SignalREventNames } from "../../../@api";

import { fieldNonexistentOrMatchingFilter } from "../../components/Shared";
import { TextMarkdown } from "../../components/Shared/Components";
import { TooltipWrapper } from "../../components/Shared/Components/TooltipWrapper/TooltipWrapper";
import { IProgressColumn, ProgressHandler } from "../../components/Shared/ProgressHandler/ProgressHandler";
import { translationKeys } from "../../models/app-translation-keys";
import { UploadFile } from "../../models/classes/upload-file.class";
import {
  IFileProgressError,
  IFileProgressSuccess,
  ILanguageOption,
  IProgressedFileResult,
} from "../../models/interfaces";
import { useSummaryService } from "../../services/ServiceHooks/summaryHook";
import { useStore } from "../../stores/StoreContext";

import "./SummaryUpload.scss";

interface ISummaryUploadProps {
  disabled: boolean;
  uploadFile: UploadFile;
  setIsFileProcessing: (id: string, isProcessing: boolean) => void;
  languageOptions: ILanguageOption[];
  initialLanguage: ILanguageOption;
  downloadFunction: (file: UploadFile, content: IProgressedFileResult) => void;
}

const RawSummaryUpload: FunctionComponent<ISummaryUploadProps> = (props: ISummaryUploadProps) => {
  const { t } = useTranslation();
  const { userStore, budgetStore, globalErrorsStore, summaryStore } = useStore();
  const summaryService = useSummaryService();

  const [isSignalRActive, setIsSignalRActive] = useState<boolean>(false);
  const [budgetLimitReached, setBudgetLimitReached] = useState<boolean>(false);
  const [progressedFileResult, setProgressedFileResult] = useState<IProgressedFileResult | undefined>(undefined);
  const [isFileProcessing, setIsFileProcessing] = useState<boolean>(false);

  const processIdRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | undefined>(undefined);
  const summarizationInProgressRef = useRef<boolean>(false);

  const MIN_OUTPUT_SENTENCES = 1;
  const MAX_OUTPUT_SENTENCES = 20;

  const [maxOutputSentences, setMaxOutputSentences] = useState<number>(3);

  const [languageOptionsTranslated, setLanguageOptionsTranslated] = useState<ILanguageOption[]>([]);
  const [selectedOutputLanguage, setSelectedOutputLanguage] = useState<ILanguageOption>({
    ...props.initialLanguage,
    label: t(props.initialLanguage.name),
  });

  useEffect(() => {
    const translatedLanguageOptions = props.languageOptions.map((lang: ILanguageOption) => ({
      ...lang,
      label: t(lang.name),
    }));
    translatedLanguageOptions.sort((a, b) => a.label.localeCompare(b.label));
    setLanguageOptionsTranslated(translatedLanguageOptions);
  }, [props.languageOptions, userStore.currentLanguage]);

  useEffect(() => {
    if (!isFileProcessing && summaryStore.containsProgressingFile(props.uploadFile)) {
      summaryStore.removeProgressingFile(props.uploadFile);
    }
    props.setIsFileProcessing(props.uploadFile.id, isFileProcessing);
  }, [isFileProcessing]);

  const setDefaultProgressValues = () => {
    processIdRef.current = null;
    summarizationInProgressRef.current = false;
    setIsFileProcessing(false);
  };

  const updateOutputSentences = (value: number) => {
    if (value < MIN_OUTPUT_SENTENCES) {
      setMaxOutputSentences(MIN_OUTPUT_SENTENCES);
    } else if (value > MAX_OUTPUT_SENTENCES) {
      setMaxOutputSentences(MAX_OUTPUT_SENTENCES);
    } else {
      setMaxOutputSentences(value);
    }
  };

  const startProcessingFile = () => {
    // Process ID is generated to track the translation process via SignalR and for cancellation if needed
    setDefaultProgressValues();

    processIdRef.current = uuidv4();
    summarizationInProgressRef.current = false;

    summaryStore.addProgressingFile(props.uploadFile);
    setProgressedFileResult(undefined);
    setIsSignalRActive(false);
    setIsFileProcessing(true);
  };

  useEffect(() => {
    const startUpload = async () => {
      try {
        abortControllerRef.current = new AbortController();

        const file = props.uploadFile.file;

        const outputLanguage = selectedOutputLanguage?.code ?? "de";

        const requestInitOverrides: RequestInit | InitOverrideFunction = {
          signal: abortControllerRef.current.signal,
          body: file,
        };

        console.debug("[Summary] Starting file upload for summarization:");
        const response: ApiResponse<string> = await summaryService.summarize(
          {
            processId: processIdRef.current!,
            fileName: encodeURIComponent(file.name),
            fileSizeInBytes: file.size,
            outputLanguage: outputLanguage,
            maxOutputSentences: maxOutputSentences,
          },
          requestInitOverrides,
        );

        if (response.raw.ok) {
          console.debug(
            "[Summary] File uploaded successfully and summarization started. Waiting for SignalR events...",
          );
        } else if (response.raw.status === 402) {
          cancelFileUploadAndOpenProcesses();

          handleBudgetLimitReached(response.raw);
          setProgressedFileResult({
            errorMessage: t(translationKeys.globalErrors.budgetErrors.outOfBudget),
          });
        } else {
          cancelFileUploadAndOpenProcesses();

          const error = await response.raw.json();
          console.error("[Summary] File upload failed. Error: ", error);
          setProgressedFileResult({
            errorMessage: t(translationKeys.upload.error.uploadFailedMarkdown),
          });
        }
      } catch (error: unknown) {
        cancelFileUploadAndOpenProcesses();

        if (error === 499) {
          console.debug("[Summary] File upload aborted");
        } else {
          console.error("[Summary] Error uploading file:", error);
          setProgressedFileResult({
            errorMessage: t(translationKeys.upload.error.uploadFailedMarkdown),
          });
        }
      }
    };

    if (isFileProcessing && isSignalRActive) {
      startUpload();
    }
  }, [isFileProcessing, isSignalRActive]);

  const handleBudgetLimitReached = useCallback((response: Response) => {
    budgetStore.setBudgetDisabled(true);
    budgetStore.errorCode = response.status;
    globalErrorsStore.addErrorIfNotExists(translationKeys.globalErrors.budgetErrors.outOfBudget);
    setBudgetLimitReached(true);
  }, []);

  const cancelFileProcessingInBackend = useCallback(async (processId: string) => {
    try {
      await summaryService.cancelActiveSummarization({ processId });

      console.debug("[Summary] Summarization processing cancelled successfully.");
    } catch (error) {
      console.error("[Summary] Failed to cancel summarization processing", error);
    }
  }, []);

  const cancelFileUploadAndOpenProcesses = useCallback(() => {
    if (processIdRef.current) {
      console.debug("[Summary] Cancel file upload and stop open signalR connections");
      abortControllerRef.current?.abort(499);

      summaryStore.removeProgressingFile(props.uploadFile);
      if (summarizationInProgressRef.current) {
        cancelFileProcessingInBackend(processIdRef.current);
      }

      setDefaultProgressValues();
    }
  }, [processIdRef.current]);

  useEffect(() => {
    // Cleanup function to cancel ongoing file upload and stop open signalR connections before unmounting
    return () => {
      cancelFileUploadAndOpenProcesses();
    };
  }, []);

  const progressHandlerMemo = useMemo(() => {
    if (!isFileProcessing) {
      return;
    }

    const processIdFilter = fieldNonexistentOrMatchingFilter("processId", processIdRef.current);

    const successCallback = (res: IFileProgressSuccess) => {
      console.debug("[Summary] succeeded: ", res);
      setProgressedFileResult({
        languageOption: selectedOutputLanguage,
        successMessage: res.result,
      });

      setDefaultProgressValues();
    };

    const errorCallback = (res: IFileProgressError) => {
      console.error("[Summary] failed: ", res);
      setProgressedFileResult({
        errorMessage: `${res.error}`,
      });

      setDefaultProgressValues();
    };

    const columns: IProgressColumn[] = [
      {
        titleKey: translationKeys.upload.progress.upload,
        progress: 0,
        hubName: "uploadfile",
        progressEndpoint: SignalREventNames.UploadFileProgress,
        successEndpoint: SignalREventNames.UploadFileSucceeded,
        errorEndpoint: SignalREventNames.UploadFileFailed,
        filter: [processIdFilter],
        successCallback: () => console.debug("[Summary] Upload completed successfully"),
        errorCallback: () => console.debug("[Summary] Upload failed"),
      },
      {
        titleKey: translationKeys.upload.progress.summarize,
        progress: 0,
        hubName: "summarization",
        progressEndpoint: SignalREventNames.SummarizationProgress,
        successEndpoint: SignalREventNames.SummarizationSucceeded,
        errorEndpoint: SignalREventNames.SummarizationFailed,
        filter: [processIdFilter],
        progressCallback: () => {
          summarizationInProgressRef.current = true;
        },
        successCallback,
        errorCallback,
      },
    ];

    return (
      <ProgressHandler
        progresscolumns={columns}
        cancelProgress={cancelFileUploadAndOpenProcesses}
        connectionSetupCompleted={() => setIsSignalRActive(true)}
      />
    );
  }, [isFileProcessing]);

  return (
    <div className="summary-processing-container">
      <div className="upload-controls">
        {!!props.languageOptions?.length && (
          <div className="language-selection">
            <label htmlFor="outputLanguage">{t(translationKeys.upload.selectOutputLanguage)}</label>

            <Dropdown
              value={selectedOutputLanguage}
              id="outputLanguage"
              onChange={(e: any) => setSelectedOutputLanguage(e.target.value)}
              options={languageOptionsTranslated}
              optionLabel="label"
              disabled={isFileProcessing || props.disabled}
              filter={true}
              filterBy="label"
              filterInputAutoFocus={true}
            />
          </div>
        )}

        <div className="sentence-selection">
          <label htmlFor="max-sentences-input">{t(translationKeys.upload.summary.maxOutputSentences)}</label>

          <InputNumber
            value={maxOutputSentences}
            className="max-sentences-input"
            onChange={(e: any) => updateOutputSentences(e.value)}
            disabled={isFileProcessing || props.disabled}
            min={MIN_OUTPUT_SENTENCES}
            max={MAX_OUTPUT_SENTENCES}
          />

          <Slider
            value={maxOutputSentences}
            disabled={isFileProcessing || props.disabled}
            min={MIN_OUTPUT_SENTENCES}
            max={MAX_OUTPUT_SENTENCES}
            onChange={(e: any) => updateOutputSentences(e.value)}
          />
        </div>

        <Button
          label={t(translationKeys.upload.summary.upload)}
          onClick={startProcessingFile}
          disabled={isFileProcessing || props.disabled}
          className="flex flex-row-reverse align-items-center gap-2"
          icon={<FontAwesomeIcon icon={faChevronRight} />}
        />
      </div>

      {progressHandlerMemo}

      {budgetLimitReached && (
        <Message severity="error" text={t(translationKeys.globalErrors.budgetErrors.outOfBudget)} />
      )}

      {progressedFileResult && (
        <div className="output-area">
          <div className="output-header">
            <span className="output-title">
              {t(translationKeys.upload.summary.output)}{" "}
              {progressedFileResult.languageOption && (
                <small>({progressedFileResult.languageOption?.code.toUpperCase()})</small>
              )}
            </span>

            {!!progressedFileResult.successMessage?.length && (
              <Fragment>
                <TooltipWrapper
                  targetRef=".output-download-icon"
                  position="top"
                  content={t(translationKeys.upload.summary.download)}
                />

                <FontAwesomeIcon
                  className="output-download-icon"
                  icon={faFileArrowDown}
                  onClick={() => props.downloadFunction(props.uploadFile, progressedFileResult!)}
                />
              </Fragment>
            )}
          </div>

          <div className="output-content">
            {!!progressedFileResult.successMessage?.length && (
              <TextMarkdown className="success-message">{progressedFileResult.successMessage}</TextMarkdown>
            )}

            {!!progressedFileResult.errorMessage?.length && (
              <TextMarkdown className="error-message">{progressedFileResult.errorMessage}</TextMarkdown>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const SummaryUpload = memo(RawSummaryUpload);
export { SummaryUpload };
